import moment from "moment-timezone";
import functions from "@/use/functions";
import useFunctions from "@/use/functions1";

const { decodeHTMLEncodedStr, getInitial } = useFunctions();

export default function useFormatter(): any {
  function formatMoney(number: number, decimals = 2): string {
    if (number == undefined) {
      number = 0;
    }

    const value = formatNumber(number, decimals);
    if (value.charAt(0) === "-") {
      return `-$${value.replace("-", "")}`;
    } else {
      return `$${value}`;
    }
  }

  function formatNumber(number: number, decimals = 2): string {
    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(number);
  }

  function formatPhoneNumber(phoneStr: string): string {
    const cleanedPhone = phoneStr.replace(/[^0-9]/g, "");

    return cleanedPhone.length === 10
      ? `(${cleanedPhone.substring(0, 3)}) ${cleanedPhone.substring(
          3,
          6
        )}-${cleanedPhone.substring(6, 10)}`
      : phoneStr;
  }
  function formatNationalIdNumber(nationalIdNumber: string): string {
    const cleanedValue = nationalIdNumber.replace(/[^0-9]/g, "");

    return cleanedValue.length === 9
      ? `(${cleanedValue.substring(0, 3)}) ${cleanedValue.substring(
          3,
          6
        )}-${cleanedValue.substring(6, 10)}`
      : nationalIdNumber;
  }
  function formatZipCode(zipStr: string): string {
    if (zipStr) {
      const cleanedZip = zipStr?.includes("-")
        ? zipStr.split("-").join("")
        : `${zipStr}`;
      return cleanedZip.length === 9
        ? `${cleanedZip.substring(0, 5)}-${cleanedZip.substring(5, 9)}`
        : zipStr;
    } else return "";
  }

  function formatDecimalAsPercentage(num) {
    return new Intl.NumberFormat("default", {
      style: "percent",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(num);
  }

  function formatDateInputDisplayValue(dateStr: string) {
    if (dateStr) {
      const dob = dateStr.split("-", 3);

      const year = dob[0];
      let month = dob[1];
      let day = dob[2];

      // Add leading zeros if month or day is a single digit
      if (month.length < 2) {
        month = "0" + month;
      }

      if (day.length < 2) {
        day = "0" + day;
      }

      return `${month}/${day}/${year}`;
    }

    return dateStr;
  }

  function formatDateOfBirth(dateStr: string) {
    return formatDateInputDisplayValue(dateStr);
  }

  function formatFullName(firstName: string, lastName: string) {
    return functions.capitalize(
      `${decodeHTMLEncodedStr(firstName) || ""} ${
        decodeHTMLEncodedStr(lastName) || ""
      }`.trim() || "No Name"
    );
  }

  function formatInitials(firstName: string, lastName: string) {
    return `${firstName?.charAt(0) || ""}${lastName?.charAt(0) || ""}`.trim();
  }

  function formatDate(date: Date) {
    if (date) {
      return moment(date.toISOString(), moment.ISO_8601)
        .tz('America/Chicago')
        .format("MMMM D, YYYY [at] h:mm A z");
    }
  }

  function formatDateTime(dateStr: string) {
    try {
      if (dateStr) {
        return moment
          .utc(dateStr, moment.ISO_8601)
          .tz("America/Chicago")
          .format("MMMM D, YYYY [at] h:mm A z");
      }
    } catch (ex) {
      console.log("Could not format date time");
      return dateStr;
    }
  }

  function formatIsoToMmDdYyyy(isoString: string) {
    if (isoString) {
      const date = new Date(isoString);

      date.setMinutes(date.getMinutes() + date.getTimezoneOffset());

      const year = date.getFullYear();
      let month = (date.getMonth() + 1).toString(); // Add 1 because getMonth() is 0-indexed
      let day = date.getDate().toString();

      // Add leading zeros if month or day is a single digit
      if (month.length < 2) {
        month = "0" + month;
      }
      if (day.length < 2) {
        day = "0" + day;
      }

      return `${month}/${day}/${year}`;
    }

    return isoString;
  }

  function componentToHex(c: number) {
    const hex = c.toString(16);
    return hex.length == 1 ? "0" + hex : hex;
  }

  function formatRgbToHex(r: number, g: number, b: number) {
    return "#" + componentToHex(r) + componentToHex(g) + componentToHex(b);
  }

  function formatHexToRgb(hex: string) {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
      ? {
          r: parseInt(result[1], 16),
          g: parseInt(result[2], 16),
          b: parseInt(result[3], 16),
        }
      : null;
  }

  return {
    formatDateOfBirth,
    formatDateTime,
    formatDateInputDisplayValue,
    formatFullName,
    formatInitials,
    formatMoney,
    formatNumber,
		formatNationalIdNumber,
    formatPhoneNumber,
    formatZipCode,
    formatDecimalAsPercentage,
    formatDate,
    formatIsoToMmDdYyyy,
		componentToHex,
		formatRgbToHex,
		formatHexToRgb
  };
}
